export default function HowItWorks() {
  return (
    <section id="how-it-works" className="py-32 relative overflow-hidden bg-gradient-to-b from-white via-[#F8FBFC] to-white">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #2B6777 1px, transparent 0)',
          backgroundSize: '48px 48px'
        }}></div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        {/* Headline */}
        <div className="mb-20">
          <h2 className="text-5xl md:text-6xl font-heading font-bold text-[#2B6777] mb-5 tracking-tight leading-tight">
            How It Works
          </h2>
          <p className="text-2xl text-[#2B6777]/80 font-medium max-w-[800px]">
            Four simple steps. Total control from start to finish.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative mb-16">
          {/* Connector Line - Desktop */}
          <div className="hidden lg:block absolute top-[100px] left-[10%] right-[10%] h-1 bg-gradient-to-r from-[#52AB98] via-[#52AB98] to-[#D98CA3] rounded-full z-0"></div>

          {/* 4 Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative z-10">

            {/* Step 1 */}
            <div className="group flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-[180px] h-[180px] rounded-2xl overflow-hidden shadow-xl group-hover:shadow-2xl transition-all duration-500 border-4 border-white">
                  <img
                    src="https://public.readdy.ai/ai/img_res/8c1ec47c64680070a1ccde92abf130a0.jpg"
                    alt="Upload photos of your home"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-10 h-10 bg-[#52AB98] rounded-full flex items-center justify-center text-white font-bold text-lg border-4 border-white">
                  1
                </div>
              </div>
              <h3 className="text-xl font-heading font-bold text-[#2B6777] mb-3 mt-4">Upload Photos</h3>
              <p className="text-sm text-[#2B6777]/70 leading-relaxed max-w-[210px]">
                Take clear photos of all four sides of your home using your phone or tablet. No measurements are needed. For best results, make sure windows are visible and free from obstructions like cars, trees, or bushes.
              </p>
            </div>

            {/* Step 2 */}
            <div className="group flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-[180px] h-[180px] rounded-2xl overflow-hidden shadow-xl group-hover:shadow-2xl transition-all duration-500 border-4 border-white">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clear%20transparent%20pricing%20document%20proposal%20on%20tablet%20screen%20showing%20itemized%20costs%20no%20hidden%20fees%20professional%20clean%20design%20modern%20interface%20homeowner%20reviewing&width=200&height=200&seq=step-pricing-v2&orientation=squarish"
                    alt="Get your quote"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-10 h-10 bg-[#52AB98] rounded-full flex items-center justify-center text-white font-bold text-lg border-4 border-white">
                  2
                </div>
              </div>
              <h3 className="text-xl font-heading font-bold text-[#2B6777] mb-3 mt-4">Get Your Quote</h3>
              <p className="text-sm text-[#2B6777]/70 leading-relaxed max-w-[210px]">
                Receive clear, transparent pricing based on your photos and preferences. No sales pressure, no surprises, just straightforward information and detailed breakdown of windows.
              </p>
            </div>

            {/* Step 3 */}
            <div className="group flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-[180px] h-[180px] rounded-2xl overflow-hidden shadow-xl group-hover:shadow-2xl transition-all duration-500 border-4 border-white">
                  <img
                    src="https://readdy.ai/api/search-image?query=Licensed%20professional%20measuring%20window%20frame%20with%20precision%20tools%20verified%20measurements%20custom%20fit%20residential%20home%20interior%20natural%20lighting%20quality%20craftsmanship%20attention%20to%20detail&width=200&height=200&seq=step-measure-v2&orientation=squarish"
                    alt="Professional measurement visit"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-10 h-10 bg-[#52AB98] rounded-full flex items-center justify-center text-white font-bold text-lg border-4 border-white">
                  3
                </div>
              </div>
              <h3 className="text-xl font-heading font-bold text-[#2B6777] mb-3 mt-4">Professional Measurement</h3>
              <p className="text-sm text-[#2B6777]/70 leading-relaxed max-w-[210px]">
                A licensed professional visits your home to verify all measurements before your custom windows are ordered, ensuring a perfect fit.
              </p>
            </div>

            {/* Step 4 */}
            <div className="group flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-[180px] h-[180px] rounded-2xl overflow-hidden shadow-xl group-hover:shadow-2xl transition-all duration-500 border-4 border-[#D98CA3]/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20window%20installation%20team%20installing%20beautiful%20new%20window%20in%20residential%20home%20expert%20craftsmanship%20clean%20worksite%20natural%20lighting%20quality%20work%20satisfied%20homeowner%20pays%20after%20completion&width=200&height=200&seq=step-install-v3&orientation=squarish"
                    alt="Professional window installation"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-10 h-10 bg-[#D98CA3] rounded-full flex items-center justify-center text-white font-bold text-lg border-4 border-white">
                  4
                </div>
              </div>
              <h3 className="text-xl font-heading font-bold text-[#2B6777] mb-3 mt-4">Installation</h3>
              <p className="text-sm text-[#2B6777]/70 leading-relaxed max-w-[210px]">
                Choose a convenient installation date. Your windows are installed by experienced professionals, and you only pay once the job is complete and you are fully satisfied.
              </p>
            </div>

          </div>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-br from-white to-[#F6D7DF]/10 rounded-3xl p-8 md:p-10 border-2 border-[#D98CA3]/20 mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#D98CA3] to-[#C97B92] rounded-2xl">
                <i className="ri-shield-check-fill text-white text-3xl"></i>
              </div>
              <div>
                <h4 className="text-2xl font-heading font-bold text-[#2B6777] mb-1">
                  <strong>NO MONEY DOWN</strong> Guarantee
                </h4>
                <p className="text-base text-[#2B6777]/70">
                  You pay nothing until your project is complete and you&apos;re <strong>100% SATISFIED</strong>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-[#2B6777]/60">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full">
                <i className="ri-time-line text-[#52AB98]"></i>
                <span>~2 min to start</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full">
                <i className="ri-bank-card-line text-[#52AB98]"></i>
                <span>No credit card</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pt-4">
          <a
            href="https://api.leadconnectorhq.com/widget/form/0ZsIY0jYLaF8aXm2FeXI"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-12 py-5 bg-[#52AB98] text-white text-lg font-bold rounded-xl hover:bg-[#478F82] transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform duration-300"
          >
            Start With Photos
            <i className="ri-camera-line text-xl"></i>
          </a>
          <p className="text-sm text-[#2B6777]/60 mt-4 font-medium">
            Takes ~2 minutes · No credit card · No in-home sales visit
          </p>
        </div>
      </div>
    </section>
  );
}
